import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './AuthContext.js';
import { homePathForRole } from './home-path.js';

/**
 * Redirige a la pantalla apropiada segun el estado de la sesion:
 *   - sin sesion → /login
 *   - temp + mfa_setup_required → /mfa/setup
 *   - temp + mfa_configured → /mfa/verify
 *   - full + super_admin fuera de /platform → /platform/tenants
 *   - full → renderiza children
 */
export function RequireFullAuth({ children }: { children: ReactNode }): JSX.Element {
  const { session } = useAuth();
  const location = useLocation();

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (session.kind === 'temp') {
    const target = session.mfaStatus === 'configured' ? '/mfa/verify' : '/mfa/setup';
    return <Navigate to={target} replace />;
  }
  // ADR-032/038: super_admin es platform-scoped — está bloqueado con 403 en todo
  // /api/v1/* (rutas de tenant). Si aterriza en una ruta de tenant (recarga de
  // sesión, catch-all, deep-link), lo enviamos a la consola de plataforma. No se
  // redirige dentro de /platform/* para evitar bucle; allí actúa RequirePlatformAdmin.
  if (session.user?.role === 'super_admin' && !location.pathname.startsWith('/platform')) {
    return <Navigate to={homePathForRole('super_admin')} replace />;
  }
  return <>{children}</>;
}

export function RequireTempAuth({ children }: { children: ReactNode }): JSX.Element {
  const { session } = useAuth();
  if (!session) {
    return <Navigate to="/login" replace />;
  }
  if (session.kind === 'full') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
